import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, MapPin, ArrowUpRight, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { TALENT_TYPES } from "@/lib/roleCategories";

const ease = [0.25, 0.1, 0.25, 1];
const fade = (delay = 0) => ({ initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.9, delay, ease } });

/** Full-bleed cinematic hero with the editorial search bar. */
export default function HeroEditorial({ cms }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [type, setType] = useState("");
  const [city, setCity] = useState("");

  const submit = (e) => {
    e.preventDefault();
    const qs = new URLSearchParams();
    if (type) qs.set("talent_type", type);
    if (city.trim()) qs.set("location", city.trim());
    const isCreator = user?.role === "creator" || user?.role === "professional" || user?.role === "admin";
    if (isCreator) return navigate(`/createurs/talents?${qs.toString()}`);
    if (user?.role === "talent") return navigate("/talent/createurs");
    toast("Créez votre compte pour explorer les talents Helencia");
    navigate(`/register?role=creator&${qs.toString()}`);
  };

  const accent = cms?.accent_color || "#C68E59";

  return (
    <section className="relative min-h-[92vh] lg:min-h-[100vh] flex items-end overflow-hidden bg-[#0F0E0D] text-[#F7F5F0]" data-testid="landing-hero">
      <motion.img
        src="/landing/hero.jpg"
        alt="Backstage d'un défilé Helencia"
        initial={{ scale: 1.08, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1.8, ease }}
        className="absolute inset-0 w-full h-full object-cover object-[60%_center]"
        fetchPriority="high"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0F0E0D] via-[#0F0E0D]/55 to-[#0F0E0D]/15" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0F0E0D]/80 via-[#0F0E0D]/20 to-transparent" />
      <div className="absolute inset-0 pointer-events-none opacity-[0.12] mix-blend-overlay" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")" }} />

      <div className="absolute top-8 right-6 sm:right-10 lg:right-14 hidden md:flex items-center gap-3 text-[10px] uppercase tracking-[0.28em] text-[#E6C29A]/90">
        <span className="h-px w-10 bg-[#E6C29A]/60" /> Backstage · Paris
      </div>

      <div className="relative w-full max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14 pb-14 sm:pb-20 pt-40">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <motion.p {...fade(0.2)} className="flex items-center gap-3 text-[10px] sm:text-xs uppercase tracking-[0.28em]" style={{ color: cms?.accent_color_dark ? "#E6C29A" : "#E6C29A" }}>
              <span className="h-px w-10" style={{ background: accent }} /> {cms?.hero_eyebrow || "La maison des talents & des créateurs"}
            </motion.p>
            <motion.h1 {...fade(0.35)} className="font-serif font-light tracking-tight leading-[0.98] mt-6 text-5xl sm:text-6xl lg:text-7xl xl:text-[6.5rem]" data-testid="hero-title">
              {cms?.hero_title_part1 || "Révèle"}{" "}
              <span className="italic text-[#E6C29A]">{cms?.hero_title_italic || "ton univers"}</span>{" "}
              {cms?.hero_title_part3 || "au monde."}
            </motion.h1>
            <motion.p {...fade(0.5)} className="mt-7 max-w-xl text-[#D8D2C8] text-base sm:text-lg font-light leading-relaxed">
              {cms?.hero_subtitle || "Mannequins, photographes, stylistes, maquilleurs, vidéastes, créateurs et marques : ici, la mode se rencontre, se caste et se crée."}
            </motion.p>

            <motion.form {...fade(0.65)} onSubmit={submit} className="mt-10 flex flex-col sm:flex-row sm:items-stretch gap-2 sm:gap-0 sm:bg-white/8 sm:backdrop-blur-xl sm:border sm:border-white/15 sm:rounded-full sm:p-1.5 max-w-2xl" data-testid="landing-hero-search">
              <label className="relative flex items-center flex-1 bg-white/8 sm:bg-transparent backdrop-blur-xl sm:backdrop-blur-none border border-white/15 sm:border-0 rounded-full sm:rounded-none px-5 h-12">
                <Search className="w-4 h-4 text-[#E6C29A] shrink-0" />
                <select value={type} onChange={(e) => setType(e.target.value)} className="appearance-none bg-transparent text-sm text-[#F7F5F0] w-full pl-3 pr-6 h-full focus:outline-none cursor-pointer" data-testid="landing-hero-category-select">
                  <option value="" className="text-black">Tous les talents</option>
                  {TALENT_TYPES.map((t) => <option key={t.value} value={t.value} className="text-black">{t.label}</option>)}
                </select>
                <ChevronDown className="w-3.5 h-3.5 text-[#B5AEA5] absolute right-5 pointer-events-none" />
              </label>
              <span className="hidden sm:block w-px my-2 bg-white/15" />
              <label className="flex items-center flex-1 bg-white/8 sm:bg-transparent backdrop-blur-xl sm:backdrop-blur-none border border-white/15 sm:border-0 rounded-full sm:rounded-none px-5 h-12">
                <MapPin className="w-4 h-4 text-[#E6C29A] shrink-0" />
                <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Paris, Lyon, Milan…" className="bg-transparent text-sm text-[#F7F5F0] placeholder:text-[#B5AEA5] w-full pl-3 h-full focus:outline-none" data-testid="landing-hero-search-input" />
              </label>
              <button type="submit" className="inline-flex items-center justify-center gap-2 rounded-full h-12 px-7 text-sm font-semibold text-[#0F0E0D] hover:text-white transition-colors" style={{ background: accent }} onMouseEnter={(e) => (e.currentTarget.style.background = "#A26F3E")} onMouseLeave={(e) => (e.currentTarget.style.background = accent)} data-testid="landing-hero-submit-button">
                Explorer <ArrowUpRight className="w-4 h-4" />
              </button>
            </motion.form>

            <motion.div {...fade(0.8)} className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-[#B5AEA5]">
              <button onClick={() => navigate("/register")} className="text-[#F7F5F0] hover:text-[#E6C29A] underline underline-offset-4 decoration-[#C68E59]/60 transition-colors" data-testid="hero-cta-register">{cms?.hero_cta_primary || "Rejoindre Helencia"}</button>
              <span className="w-1 h-1 rounded-full bg-[#C68E59]" />
              <button onClick={() => navigate("/login")} className="hover:text-[#E6C29A] transition-colors" data-testid="hero-cta-login">{cms?.hero_cta_secondary || "Se connecter"}</button>
              <span className="w-1 h-1 rounded-full bg-[#C68E59] hidden sm:block" />
              <span className="hidden sm:block">Gratuit · Sans agence · Sans engagement</span>
            </motion.div>
          </div>

          <motion.div {...fade(0.9)} className="lg:col-span-4 hidden lg:flex flex-col items-end gap-6 text-right">
            <div className="border-r border-[#C68E59]/50 pr-5">
              <p className="font-serif text-5xl leading-none">7</p>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#B5AEA5] mt-2">métiers de la mode</p>
            </div>
            <div className="border-r border-[#C68E59]/50 pr-5">
              <p className="font-serif text-5xl leading-none">1</p>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#B5AEA5] mt-2">scène pour se rencontrer</p>
            </div>
            <a href="#personas" className="group inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] text-[#E6C29A]" data-testid="hero-scroll-cue">
              Trouvez votre place <span className="w-10 h-10 rounded-full border border-[#E6C29A]/50 flex items-center justify-center group-hover:bg-[#E6C29A] group-hover:text-[#0F0E0D] transition-colors"><ChevronDown className="w-4 h-4 animate-bounce" /></span>
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
